// Usage statistics (Stage 1a gate): week-4 retention by signup week, actives, and the
// restaurants people open that have no menu (input to sourcing). Read-only; definitions in
// docs/ANALYTICS_AND_MONITORING_PLAN.md §3.

import { useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { AdminShell } from '@/components/AdminShell';
import { getUsageReport, type UsageReport } from '@/api/admin';
import type { ApiError } from '@/api/client';

const pct = (x: number | null) => (x == null ? '' : `${Math.round(x * 100)}%`);

export function UsagePage() {
  const q = useQuery<UsageReport, ApiError>({
    queryKey: ['admin', 'usage'],
    queryFn: ({ signal }) => getUsageReport(signal),
  });

  return (
    <AdminShell>
      <header className="mb-6">
        <h1 className="text-2xl font-semibold tracking-tight">Usage</h1>
        <p className="mt-1 text-sm text-slate-500">
          Signed-in activity since {q.data?.measurement_start ?? '…'} · internal, admin and opted-out accounts excluded
        </p>
      </header>
      {q.isLoading && <p className="text-slate-500">Loading…</p>}
      {q.error && <p className="text-red-600">Could not load: {q.error.message}</p>}
      {q.data && <Report r={q.data} />}
    </AdminShell>
  );
}

function Report({ r }: { r: UsageReport }) {
  return (
    <div className="flex flex-col gap-6">
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <Stat label="Active today" value={r.actives.dau} />
        <Stat label="Active 7 days" value={r.actives.wau} />
        <Stat label="Active 30 days" value={r.actives.mau} />
        <Stat
          label="Platforms (30 days)"
          value={r.platform_30d.length ? r.platform_30d.map((p) => `${p.platform} ${p.users}`).join(' · ') : '–'}
        />
      </div>

      <Section
        title="Retention by signup week"
        hint="Week 4 (days 21–27 after signup) is the Stage 1a gate: stop below 10%. Grey cells are not complete yet (count so far in brackets)."
      >
        {r.cohorts.length === 0 ? (
          <Empty>No cohorts yet.</Empty>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr className="text-left">
                <Th>Signup week</Th>
                <Th className="text-right">Users</Th>
                {r.cohorts[0]!.weeks.map((w) => (
                  <Th key={w.week} className={'text-right ' + (w.week === 4 ? 'bg-amber-50' : '')}>
                    W{w.week}
                  </Th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {r.cohorts.map((c) => (
                <tr key={c.week_start}>
                  <Td className="font-mono text-xs">{c.week_start}</Td>
                  <Td className="text-right tabular-nums">{c.size}</Td>
                  {c.weeks.map((w) => (
                    <Td
                      key={w.week}
                      className={
                        'text-right tabular-nums ' +
                        (w.week === 4 ? 'bg-amber-50 font-semibold ' : '') +
                        (w.complete ? '' : 'text-slate-300')
                      }
                      title={`${w.retained} of ${c.size}${w.complete ? '' : ' (incomplete)'}`}
                    >
                      {w.complete ? pct(w.rate) : `(${w.retained})`}
                    </Td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>

      <Section title="Opened without a menu (30 days)" hint="Ranked by signed-in users, then opens. Feed for sourcing.">
        {r.coverage_demand.length === 0 ? (
          <Empty>Nothing yet.</Empty>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr className="text-left">
                <Th>Restaurant</Th>
                <Th>Area</Th>
                <Th className="text-right">Users</Th>
                <Th className="text-right">Opens</Th>
                <Th className="text-right">Anonymous opens</Th>
                <Th>Menu now</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {r.coverage_demand.map((c) => (
                <tr key={c.restaurant_id}>
                  <Td>
                    <span className="font-medium">{c.name ?? `#${c.restaurant_id}`}</span>
                    <span className="ml-2 font-mono text-xs text-slate-500">{c.slug}</span>
                  </Td>
                  <Td className="text-slate-500">{c.area ?? c.market ?? ''}</Td>
                  <Td className="text-right tabular-nums">{c.users}</Td>
                  <Td className="text-right tabular-nums">{c.user_opens}</Td>
                  <Td className="text-right tabular-nums">{c.anon_opens}</Td>
                  <Td className={c.has_menu_now ? 'text-green-700' : 'text-slate-400'}>
                    {c.has_menu_now ? 'yes (added since)' : 'no'}
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>

      <div className="grid gap-6 lg:grid-cols-3">
        <Section title="Opens with a menu (30 days)">
          <SmallTable
            head={['Area', 'Opens', 'With menu']}
            rows={(r.menu_share_by_area_30d ?? r.menu_share_30d.map((m) => ({ ...m, area: m.market ?? '?' }))).map((m) => [
              m.area,
              m.opens,
              m.opens ? `${m.with_menu} (${pct(m.with_menu / m.opens)})` : '0',
            ])}
          />
        </Section>
        <Section title="Signups by week">
          <SmallTable head={['Week', 'Signups']} rows={r.signups_by_week.map((s) => [s.week_start, s.signups])} />
        </Section>
        <Section title="Counters (30 days)">
          <SmallTable head={['Kind', 'Count']} rows={r.counters_30d.map((c) => [c.kind, c.n])} />
        </Section>
      </div>

      <ul className="list-disc pl-5 text-xs text-slate-500">
        {r.notes.map((n) => (
          <li key={n}>{n}</li>
        ))}
      </ul>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="card p-4">
      <p className="text-xs font-medium uppercase tracking-wider text-slate-500">{label}</p>
      <p className="mt-1 text-xl font-semibold tabular-nums">{value}</p>
    </div>
  );
}

function Section({ title, hint, children }: { title: string; hint?: string; children: ReactNode }) {
  return (
    <section className="card overflow-x-auto">
      <div className="border-b border-slate-100 px-4 py-3">
        <h2 className="font-semibold">{title}</h2>
        {hint && <p className="text-xs text-slate-500">{hint}</p>}
      </div>
      {children}
    </section>
  );
}

function SmallTable({ head, rows }: { head: string[]; rows: Array<Array<ReactNode>> }) {
  if (rows.length === 0) return <Empty>Nothing yet.</Empty>;
  return (
    <table className="w-full text-sm">
      <thead className="bg-slate-50 text-slate-600">
        <tr className="text-left">
          {head.map((h, i) => (
            <Th key={h} className={i ? 'text-right' : ''}>
              {h}
            </Th>
          ))}
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-100">
        {rows.map((row, i) => (
          <tr key={i}>
            {row.map((cell, j) => (
              <Td key={j} className={j ? 'text-right tabular-nums' : 'font-mono text-xs'}>
                {cell}
              </Td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Empty({ children }: { children: ReactNode }) {
  return <p className="p-4 text-sm text-slate-500">{children}</p>;
}

function Th({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <th className={'px-4 py-2 text-xs font-medium uppercase tracking-wider ' + className}>{children}</th>;
}

function Td({ children, className = '', title }: { children: ReactNode; className?: string; title?: string }) {
  return (
    <td className={'px-4 py-2 align-top ' + className} title={title}>
      {children}
    </td>
  );
}
